import React, { useState, useEffect, useRef } from "react";
import { MyPrizePage } from "../../api";
import { getCurrentUid } from "../../../../api/web-h5-common";
import { getPrizeText, formateDate, formatTs, jumpPage } from "../../../../utils/common";
import EmptyUsers from "../../../../components/empty-users";
import { PrizeShowInfo, RewardRecord, MyRewardProps } from "../../types";
import "./index.less";

const MyReward: React.FC<MyRewardProps> = ({
  title = "我的奖励"
}) => {
  const [rewardList, setRewardList] = useState<RewardRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [custId, setCustId] = useState("");
  // 分页相关状态
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(8);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const isRequesting = useRef(false);

  // 获取我的奖励列表
  const fetchMyPrizeList = async (currentPageNo: number = 1, isLoadMore = false) => {
    if (!custId || isRequesting.current) return;

    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
      setPageNo(1);
      setHasMore(true);
    }

    isRequesting.current = true;

    try {
      // 实际环境中调用真实接口
      const response = await MyPrizePage({
        activityType: "IR",
        custId: custId,
        pageNo: currentPageNo,
        pageSize: pageSize
      });

      console.log('获取我的奖励列表数据 - 页码:', currentPageNo, '返回数据:', response);

      if ([0, 200].includes(response.code)) {
        const newData = response.data.records || [];

        if (isLoadMore) {
          // 加载更多时，累加数据
          setRewardList(prevList => [...prevList, ...newData]);
        } else {
          // 首次加载或刷新时，直接设置数据
          setRewardList(newData);
        }

        // 更新分页状态 - 根据返回的数据长度判断是否还有更多
        const hasMoreData = newData.length >= pageSize;
        setHasMore(hasMoreData);

        if (isLoadMore) {
          setPageNo(currentPageNo + 1);
        } else {
          setPageNo(2); // 下次加载更多从第2页开始
        }
      }
    } catch (error) {
      console.error('获取我的奖励列表失败:', error);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isRequesting.current = false;
    }
  };

  // 处理列表滚动，实现加载更多
  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isRequesting.current || !hasMore || isLoadingMore) return;

    const { target } = e;
    const element = target as HTMLDivElement;

    // 当滚动到底部附近时加载更多
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 50) {
      fetchMyPrizeList(pageNo, true);
    }
  };

  // 获取用户ID
  useEffect(() => {
    getCurrentUid().then(uid => {
      setCustId(uid);
    });
  }, []);

  // 当获取到用户ID后，调用接口
  useEffect(() => {
    if (custId) {
      fetchMyPrizeList();
    }
  }, [custId]);

  // 获取奖励描述文案 - 根据 giveType 和 extMsg 内容
  const getDescription = (item: RewardRecord): any => {
    const { giveType, extMsg } = item;

    // 格式化周期时间范围 - 使用 common 中的 formatDate
    const formatCyclePeriod = (startTime?: number, endTime?: number): string => {
      if (!startTime || !endTime) return '';
      return `${formateDate(startTime)}~${formateDate(endTime)}`;
    };

    switch (giveType) {
      case 1: // 被邀请人奖励
        // 格式：好友【邀请人加密手机号】赠送
        // 举例：好友156****1234赠送
        if (extMsg?.mobile) {
          return `好友${extMsg.mobile}赠送`;
        }
        break;
      case 2: // 单任务奖励（邀请奖励）
        // 格式：【被邀请人加密手机号】邀请成功
        if (extMsg?.mobile) {
          return `${extMsg.mobile}邀请成功`;
        }
        break
      case 3: // 邀请人阶梯奖励
        // 格式：【统计周期】累计邀请【累计数值】人
        // 举例：2025-05-05~2025-05-11累计邀请3人
        const cyclePeriod2 = formatCyclePeriod(extMsg?.startTime, extMsg?.endTime);
        if (cyclePeriod2 && extMsg?.achieveThreshold !== undefined) {
          return `${cyclePeriod2}累计邀请${extMsg.achieveThreshold}人`;
        }
        break;
      case 4: // 邀请人榜单奖励
        // 格式：【统计周期】总排名第【排名数值】名
        // 举例：2025-05-05~2025-05-11排第5名
        const cyclePeriod3 = formatCyclePeriod(extMsg?.startTime, extMsg?.endTime);
        if (cyclePeriod3 && (extMsg?.cycleRanking || extMsg?.maxRanking)) {
          return `${cyclePeriod3}排第${extMsg?.cycleRanking || extMsg.maxRanking}名`;
        }
        break;
      default:
        // 未知类型，使用原有描述或默认文案
        return ''
    }
  };

  // 格式化奖励标题 - 支持可点击跳转的奖励
  const formatRewardTitle = (prizeShowInfoList: PrizeShowInfo[]): JSX.Element => {
    return (
      <>
        {prizeShowInfoList.map((prize, index) => {
          const prizeText = getPrizeText(prize) || prize.prizeName;
          const isClickable = (prize.prizeType === 4 || prize.prizeType === 13) && !!prize.jumpUrl;

          return (
            <React.Fragment key={index}>
              {index > 0 && '、'}
              {isClickable ? (
                <span
                  className="clickable-prize"
                  onClick={(e) => {
                    e.stopPropagation();
                    jumpPage(prize.jumpUrl);
                  }}
                >
                  {prizeText}
                </span>
              ) : (
                <span>{prizeText}</span>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  return (
    <div className="my-reward">
      <div className="reward-list" onScroll={handleListScroll}>
        {loading ? (
          <div className="loading">
            <div className="loading-icon"></div>
          </div>
        ) : rewardList.length > 0 ? (
          <>
            {rewardList.map((item, index) => (
              <div key={index} className="list-item">
                <div className="first-row">
                  <div className={`reward-title`}>
                    {formatRewardTitle(item.prizeShowInfoList)}
                  </div>
                  {item.prizeStatus === 0 && <div className={`status-text`}>
                    发放中
                  </div>}
                  {item.prizeStatus === 2 && <div className={`status-text`}>
                    发放取消
                  </div>}
                </div>

                <div className="second-row">
                  <div className="reward-desc">{getDescription(item)}</div>
                  <div className="reward-time">{formatTs(item.giveTime)}</div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <EmptyUsers emptyText="暂无奖励记录" />
        )}
      </div>
    </div>
  );
};

export default MyReward;
